import { arc, pie } from "d3-shape"

type DonutSlice = {
  id: string
  value: number
  color: string
}

type DonutProps = {
  size?: number
  strokeWidth?: number
  slices: DonutSlice[]
  onSliceHover?: (id: string | null) => void
  padAngle?: number // Gap between slices
  cornerRadius?: number // Roundness of the edges
}

export function Donut({
  size = 160,
  strokeWidth = 32,
  slices,
  onSliceHover,
  padAngle = 0.06,
  cornerRadius = 4,
}: DonutProps) {
  const radius = size / 2
  const innerRadius = size / 2.3 - strokeWidth

  const hasData = slices.length > 0 && slices.some((s) => s.value > 0)

  if (!hasData) {
    // Math generator for empty state ring
    const emptyArcGenerator = arc<any>().innerRadius(innerRadius).outerRadius(radius)

    const pathData = emptyArcGenerator({ startAngle: 0, endAngle: 2 * Math.PI })

    return (
      <svg width={size} height={size} overflow="visible">
        <g transform={`translate(${size / 2} ${size / 2})`}>
          {pathData && (
            <path
              d={pathData}
              fill="var(--interactive-background-neutral-faded, #1A1A1A)" // Dark interior
              stroke="var(--interactive-border-neutral-highlighted, #404040)" // Shinier border
              strokeWidth={1.5}
            />
          )}
        </g>
      </svg>
    )
  }

  // 1. Calculate the start and end angles for each slice based on its value
  const pieGenerator = pie<DonutSlice>()
    .value((d) => d.value)
    .sort(null) // Keep the slices in the order they were passed

  // 2. Create the math generator for the rounded, padded SVG paths
  const arcGenerator = arc<any>()
    .innerRadius(innerRadius)
    .outerRadius(radius)
    .cornerRadius(cornerRadius)
    .padAngle(padAngle)

  // 3. Generate our pie data
  const pieArcs = pieGenerator(slices)

  return (
    <svg width={size} height={size} overflow="visible">
      {/* Translate to the center of the SVG canvas */}
      <g transform={`translate(${size / 2} ${size / 2})`}>
        {pieArcs.map((pieArc, index) => {
          const slice = pieArc.data
          const pathData = arcGenerator(pieArc)

          if (!pathData) return null // Type safety

          return (
            <path
              key={index}
              d={pathData}
              onMouseEnter={() => onSliceHover?.(slice.id)}
              onMouseLeave={() => onSliceHover?.(null)}
              stroke={slice.color}
              strokeWidth={2} // Creates the bold outer border
              fill={slice.color}
              fillOpacity={0.15} // Semi-transparent inner section
            />
          )
        })}
      </g>
    </svg>
  )
}
